import dayjs from 'dayjs';
import { isNil } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { BackCard } from '../../components/BackCard/BackCard';
import Layout, { OrderRequestResult } from '../../components/Layout/Layout';
import {
  extractLatestServiceAttemptIfExist,
  getCorrectStatus,
  getExpectedServiceDateAndTimeSlot,
  getProblem,
} from '../../helpers/order';
import { useLanguage } from '../../locale/useLanguage';
import { BlockedReason, CalculatedOrderStatus, Problem } from '../../models';
import { Routes } from '../../routes/types';

type DetailsViewProps = {};

export const DetailsView: React.FC<DetailsViewProps> = () => {
  const history = useHistory();
  const [t] = useLanguage();

  const [{ data: order }, setOrderRequestData] = useState<OrderRequestResult>({
    isLoading: true,
    isFetching: false,
  });

  const completeName = order?.mlkObject
    ? order?.mlkObject.recipient.firstName +
      ' ' +
      order?.mlkObject.recipient.lastName
    : undefined;
  const address = order?.deliveryAddress.address;
  const merchantName = order?.shipper.alias || order?.shipper.name;
  const attemptNumber = order?.attempt;
  const trackingNumber = order?.shipperOrderCode;
  const isLastAttempt = order?.blockedReasons?.includes(BlockedReason.B01);
  const problem = getProblem(order);
  const pinCode = order?.securityPin;
  const delay = order?.delay;
  const intercomName = order?.deliveryAddress.doorbell;
  const driverName =
    order?.driver?.firstName &&
    `${order?.driver?.firstName} ${
      order?.driver?.lastName.length > 0 && order?.driver?.lastName.charAt(0)
    }.`;
  const driverImage = order?.driver?.imageHappy;
  const latestAttempt = extractLatestServiceAttemptIfExist(order);

  const calculatedOrderStatus = useMemo(() => {
    return order && getCorrectStatus(order);
  }, [order]);

  useEffect(() => {
    if (calculatedOrderStatus === CalculatedOrderStatus.Deleted) {
      history.replace(Routes.Home);
    }
  }, [calculatedOrderStatus, history]);

  const expectedServiceDateAndTimeSlot = useMemo(() => {
    return getExpectedServiceDateAndTimeSlot(order);
  }, [order]);

  const noteForCustomer = useMemo(() => {
    if (calculatedOrderStatus === CalculatedOrderStatus.Fulfilled) {
      if (order?.serviceAttempts && order.serviceAttempts.length > 0) {
        return order.serviceAttempts[0].noteForCustomer;
      }
    }
    return undefined;
  }, [calculatedOrderStatus, order?.serviceAttempts]);

  const poInfo = useMemo(() => {
    if (
      order &&
      order.blockedReasons?.includes(BlockedReason.B01) &&
      (problem === Problem.F01 || problem === Problem.F02) &&
      order.fallbackParameters &&
      order.fallbackParameters.fallbackFirstAvailabilityInPostalOffice
    ) {
      const dateRange = dayjs(
        order.fallbackParameters.fallbackFirstAvailabilityInPostalOffice
      );
      return {
        poName: order.fallbackParameters.fallbackAddress,
        poFromDate: dateRange,
      };
    }
  }, [order, problem]);

  const onBackClick = useCallback(() => {
    history.push(Routes.Home);
  }, [history]);

  const goToVerifyDelivery = useCallback(() => {
    history.push(Routes.VerifyDelivery);
  }, [history]);

  const onEditIntercomName = useCallback(() => {
    history.push(Routes.ChangeAddress);
  }, [history]);

  const onEditAddress = useCallback(() => {
    history.push(Routes.ChangeAddress);
  }, [history]);

  const goToHelp = useCallback(() => {
    if (order) {
      history.push(Routes.Help.replace(':mlkId', order.opaqueId));
    } else {
      history.push(Routes.HelpSearchOrder);
    }
  }, [history, order]);

  return (
    <Layout
      title={t('pageTitles.orderDetails')}
      hasBack
      hasHelp
      onBackClick={onBackClick}
      onHelpClick={goToHelp}
      onOrderChange={setOrderRequestData}
    >
      <BackCard
        poName={poInfo?.poName}
        isLastAttempt={isLastAttempt}
        instructions={order?.deliveryAddress.instructions}
        withdrawDate={poInfo?.poFromDate.format('DD/MM/YYYY')}
        withdrawHour={poInfo?.poFromDate.format('HH:mm')}
        noteForCustomer={noteForCustomer}
        attempt={attemptNumber!}
        merchantName={merchantName}
        deliveryAddress={address!}
        trackingNumber={trackingNumber!}
        expectedServiceDateAndTimeSlot={expectedServiceDateAndTimeSlot}
        driverName={driverName}
        driverImage={driverImage}
        intercomName={intercomName!}
        orderState={calculatedOrderStatus!}
        completeName={completeName}
        problem={problem}
        pinCode={pinCode!}
        isServiceAttemptExist={!isNil(latestAttempt)}
        delay={delay}
        onVerifyDelivery={goToVerifyDelivery}
        onEditIntercomName={onEditIntercomName}
        onEditAddressClick={onEditAddress}
      />
    </Layout>
  );
};
